import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useSite } from '@/contexts/SiteContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  RefreshCw, 
  MessageSquare, 
  Target, 
  Clock, 
  TrendingUp,
  Search,
  Filter,
  Calendar,
  Database,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import RagSourcesViewer from '../components/chatbot/RagSourcesViewer';

import { API_URL } from '../config/api';

// Use the centralized API configuration
const apiUrl = API_URL;

interface ChatbotLog {
  id: string;
  trace_id: string;
  page_id: string;
  session_id: string;
  customer_message: string;
  reply: string;
  used_adjacent_topics: string[];
  focused_element_id?: string;
  enhanced: boolean;
  sources: any[];
  navigation_actions: any[];
  confidence?: number;
  created_at: string;
  metadata?: any;
  rag_summary?: any;
  page_url?: string;
  page_title?: string;
  site_id?: string;
  site_name?: string;
  site_url?: string;
}

interface Site {
  id: string;
  name: string;
  url: string;
  log_count: number;
}

interface ChatbotLogsResponse {
  logs: ChatbotLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

const ChatbotLogs: React.FC = () => {
  const { selectedSite } = useSite();
  const [logs, setLogs] = useState<ChatbotLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ChatbotLog | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });
  
  // Filters
  const [sessionIdFilter, setSessionIdFilter] = useState('');
  const [pageIdFilter, setPageIdFilter] = useState('');
  const [enhancedFilter, setEnhancedFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting
  const [sortField, setSortField] = useState<keyof ChatbotLog | ''>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Sorting functions
  const handleSort = (field: keyof ChatbotLog) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const getSortIcon = (field: keyof ChatbotLog) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-muted-foreground" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 ml-1 text-primary" /> : 
      <ChevronDown className="h-4 w-4 ml-1 text-primary" />;
  };
  
  const sortLogs = (logs: ChatbotLog[]) => {
    if (!sortField) return logs;
    
    return [...logs].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle different data types
      if (sortField === 'created_at') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      } else if (sortField === 'confidence') {
        aValue = (aValue as number) || 0;
        bValue = (bValue as number) || 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const fetchLogs = async (offset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: offset.toString()
      });

      if (sessionIdFilter) params.append('sessionId', sessionIdFilter);
      if (pageIdFilter) params.append('pageId', pageIdFilter);
      if (selectedSite) params.append('siteId', selectedSite._id || selectedSite.id || '');
      if (enhancedFilter !== 'all') params.append('enhanced', enhancedFilter);

      const response = await fetch(`${apiUrl}/chatbot-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chatbot logs');
      }

      const data: ChatbotLogsResponse = await response.json();
      
      if (offset === 0) {
        setLogs(data.logs);
      } else {
        setLogs(prev => [...prev, ...data.logs]);
      }
      
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching chatbot logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogDetails = async (logId: string) => {
    try {
      const response = await fetch(`${apiUrl}/chatbot-logs/${logId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch log details');
      }

      const data = await response.json();
      setSelectedLog(data.log);
    } catch (error) {
      console.error('Error fetching log details:', error);
    }
  };

  // Site selection is now handled by global context

  useEffect(() => {
    fetchLogs();
  }, [sessionIdFilter, pageIdFilter, selectedSite, enhancedFilter]);

  const handleRefresh = () => {
    fetchLogs(0);
  };

  const handleLoadMore = () => {
    fetchLogs(pagination.offset + pagination.limit);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return {
        formatted: format(date, 'MMM d, yyyy h:mm a'),
        relative: formatDistanceToNow(date, { addSuffix: true })
      };
    } catch (error) {
      return {
        formatted: timestamp,
        relative: 'Unknown'
      };
    }
  };

  const getEnhancedBadge = (log: ChatbotLog) => {
    if (!log.enhanced) {
      return <Badge variant="outline" className="text-xs">Basic</Badge>;
    }
    
    const sourcesCount = log.sources?.length || 0;
    if (sourcesCount === 0) {
      return <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700">Enhanced (No Sources)</Badge>;
    }
    
    return (
      <Badge className="text-xs bg-green-100 text-green-800">
        Enhanced ({sourcesCount} sources)
      </Badge>
    );
  };

  const getConfidenceBadge = (confidence?: number) => {
    if (!confidence) return null;
    
    const percentage = (confidence * 100).toFixed(1);
    const colorClass = confidence >= 0.8 
      ? 'bg-green-100 text-green-800'
      : confidence >= 0.6 
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-red-100 text-red-800';
    
    return (
      <Badge variant="outline" className={`text-xs ${colorClass}`}>
        {percentage}% confidence
      </Badge>
    );
  };

  // Filter and sort logs
  const filteredLogs = sortLogs(logs.filter(log => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      log.customer_message?.toLowerCase().includes(query) ||
      log.reply?.toLowerCase().includes(query) ||
      log.trace_id?.toLowerCase().includes(query) ||
      log.session_id?.toLowerCase().includes(query)
    );
  }));

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Chatbot Logs</h1>
          <p className="text-muted-foreground">
            Monitor chatbot interactions and RAG document usage
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Search</label>
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Session ID</label>
              <Input
                placeholder="Filter by session..."
                value={sessionIdFilter}
                onChange={(e) => setSessionIdFilter(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Page ID</label>
              <Input
                placeholder="Filter by page..."
                value={pageIdFilter}
                onChange={(e) => setPageIdFilter(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select value={enhancedFilter} onValueChange={setEnhancedFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="true">Enhanced (RAG)</SelectItem>
                  <SelectItem value="false">Basic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Chatbot Interactions
            </CardTitle>
            <Badge variant="outline">
              {filteredLogs.length} of {pagination.total} logs
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Time
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('site_name')}
                  >
                    <div className="flex items-center">
                      Site
                      {getSortIcon('site_name')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('customer_message')}
                  >
                    <div className="flex items-center">
                      Message
                      {getSortIcon('customer_message')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('enhanced')}
                  >
                    <div className="flex items-center">
                      Type
                      {getSortIcon('enhanced')}
                    </div>
                  </TableHead>
                  <TableHead>RAG Sources</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('confidence')}
                  >
                    <div className="flex items-center">
                      Confidence
                      {getSortIcon('confidence')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('session_id')}
                  >
                    <div className="flex items-center">
                      Session
                      {getSortIcon('session_id')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => {
                    const time = formatTimestamp(log.created_at);
                    
                    return (
                      <TableRow 
                        key={log.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => fetchLogDetails(log.id)}
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium text-sm">{time.formatted}</div>
                            <div className="text-xs text-muted-foreground">{time.relative}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {log.site_name || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md">
                            <div className="font-medium text-sm truncate">
                              {log.customer_message}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {log.reply}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getEnhancedBadge(log)}
                        </TableCell>
                        <TableCell>
                          {log.sources && log.sources.length > 0 ? (
                            <div className="flex items-center">
                              <Target className="h-4 w-4 mr-1" />
                              <span className="text-sm">{log.sources.length}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getConfidenceBadge(log.confidence)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-mono">
                            {log.session_id.substring(0, 8)}...
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      {loading ? (
                        <div className="flex justify-center items-center">
                          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No chatbot logs found</span>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {pagination.hasMore && (
            <div className="mt-4 text-center">
              <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Chatbot Log Details
              {selectedLog && getEnhancedBadge(selectedLog)}
            </DialogTitle>
            <DialogDescription>
              {selectedLog && formatTimestamp(selectedLog.created_at).formatted}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Interaction Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Customer Message:</label>
                    <div className="mt-1 p-3 bg-muted rounded-md">
                      {selectedLog.customer_message}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Chatbot Reply:</label>
                    <div className="mt-1 p-3 bg-muted rounded-md">
                      {selectedLog.reply}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Site:</span>
                      <div className="font-medium">{selectedLog.site_name || 'Unknown'}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Page:</span>
                      <div className="font-medium">
                        {selectedLog.page_title || 'Untitled'}
                        {selectedLog.page_url && (
                          <a 
                            href={selectedLog.page_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="ml-2 text-blue-600 hover:underline text-xs"
                          >
                            View Page →
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Trace ID:</span>
                      <div className="font-mono text-xs">{selectedLog.trace_id}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Session ID:</span>
                      <div className="font-mono text-xs">{selectedLog.session_id}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Page ID:</span>
                      <div className="font-mono text-xs">{selectedLog.page_id}</div>
                    </div>
                    {selectedLog.confidence && (
                      <div>
                        <span className="text-muted-foreground">Confidence:</span>
                        <div>{(selectedLog.confidence * 100).toFixed(1)}%</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RAG Sources */}
              {selectedLog.enhanced && (
                <RagSourcesViewer 
                  sources={selectedLog.sources || []} 
                  confidence={selectedLog.confidence}
                />
              )}

              {/* Navigation Actions */}
              {selectedLog.navigation_actions && selectedLog.navigation_actions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Navigation Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedLog.navigation_actions.map((action, index) => (
                        <div key={index} className="flex items-center p-2 bg-muted rounded">
                          <Badge variant="outline" className="mr-2">
                            {action.type}
                          </Badge>
                          <span className="text-sm">{action.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Adjacent Topics */}
              {selectedLog.used_adjacent_topics && selectedLog.used_adjacent_topics.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Adjacent Topics Used</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedLog.used_adjacent_topics.map((topic, index) => (
                        <Badge key={index} variant="outline">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatbotLogs;